import React, { useState } from 'react';
import axios from 'axios';
import * as Icons from 'react-feather';
import { Modal, ModalBody, ModalHeader, ModalTitle } from 'react-bootstrap';
import FileUploadModal from '../../../shared/file-upload/FileUploadModal';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { appendApplicationId, getApiBaseUrl } from '../../../utils/apiConfig';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { buildVendorLogoUrlFromUpload, resolveVendorLogoDisplayUrl } from './utils/vendorHelpers';

function BooleanToggle({ id, label, checked, onChange, disabled }) {
  return (
    <div className="mb-3">
      <label className="form-label d-block">{label}</label>
      <div className="status-toggle modal-status">
        <input
          type="checkbox"
          id={id}
          className="check"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={id} className="checktoggle" />
      </div>
    </div>
  );
}

function VendorLogoField({ formData, sendDataToParent, readOnly = false }) {
  const [showUpload, setShowUpload] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [alert, setAlert] = useState(null);

  const propagate = (next) => {
    if (sendDataToParent) sendDataToParent(next);
  };

  const handleLogoChange = (e) => {
    propagate({ ...formData, VendorLogo: e.target.value });
  };

  const logoPreviewUrl = resolveVendorLogoDisplayUrl(formData.VendorLogo);

  const handleUploadConfirm = async ({ files }) => {
    if (!files?.length) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('entityId', formData.VendorId || 0);
      formDataToSend.append('entityType', 'ec_vendor');
      formDataToSend.append('fileCategory', 'vendor-logo');
      formDataToSend.append('UserId', getStorageValues('userId'));
      files.forEach((file) => formDataToSend.append('files', file, file.name));
      appendApplicationId(formDataToSend);

      const uploadRes = await axios.post(`${getApiBaseUrl()}/file/upload`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedList = uploadRes.data?.data || [];

      if (!uploadedList.length) {
        CreateAlert('No files were uploaded', 'Warning', setAlert);
        return;
      }

      const uploaded = uploadedList[0];
      if (uploaded?.FilePath) {
        propagate({
          ...formData,
          VendorLogo: buildVendorLogoUrlFromUpload(uploaded.FilePath).trim(),
        });
        CreateAlert('Logo URL updated from upload', 'Success', setAlert);
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to upload logo', 'Error', setAlert);
    }
  };

  if (readOnly) {
    return (
      <>
        <SKAlerts alert={alert} />
        <div className="col-lg-12 mb-2">
          <strong>Logo URL:</strong> {formData.VendorLogo || '-'}
          {logoPreviewUrl ? (
            <div className="mt-2 d-flex align-items-center gap-2">
              <img
                src={logoPreviewUrl}
                alt="Vendor logo"
                className="border rounded"
                style={{ maxHeight: 80, maxWidth: 120 }}
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                onClick={() => setShowPreview(true)}
                title="Preview logo"
                aria-label="Preview logo"
              >
                <Icons.Eye size={16} />
              </button>
            </div>
          ) : null}
        </div>
        <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size="lg">
          <ModalHeader closeButton>
            <ModalTitle>Logo Preview</ModalTitle>
          </ModalHeader>
          <ModalBody className="text-center">
            <img src={logoPreviewUrl} alt="Vendor logo preview" className="img-fluid border rounded" />
          </ModalBody>
        </Modal>
      </>
    );
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="mb-3">
        <label className="form-label">Logo URL</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            name="VendorLogo"
            value={formData.VendorLogo || ''}
            onChange={handleLogoChange}
            placeholder="Select single or multiple images"
          />
          <button
            type="button"
            className="btn btn-outline-primary d-flex align-items-center justify-content-center"
            onClick={() => setShowUpload(true)}
            title="Upload logo"
            aria-label="Upload logo"
          >
            <Icons.Upload size={18} />
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
            onClick={() => setShowPreview(true)}
            disabled={!formData.VendorLogo}
            title="Preview logo"
            aria-label="Preview logo"
          >
            <Icons.Eye size={18} />
          </button>
        </div>
        {logoPreviewUrl ? (
          <img
            src={logoPreviewUrl}
            alt="Vendor logo"
            className="mt-2 border rounded"
            style={{ maxHeight: 80, maxWidth: 120 }}
          />
        ) : null}
      </div>

      <FileUploadModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        categories={['vendor-logo']}
        onConfirm={handleUploadConfirm}
      />

      <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size="lg">
        <ModalHeader closeButton>
          <ModalTitle>Logo Preview</ModalTitle>
        </ModalHeader>
        <ModalBody className="text-center">
          {logoPreviewUrl ? (
            <img src={logoPreviewUrl} alt="Vendor logo preview" className="img-fluid border rounded" />
          ) : (
            <span className="text-muted">No logo to preview.</span>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

function VendorDetailsFields({ formData, sendDataToParent, readOnly = false }) {
  const propagate = (next) => {
    if (sendDataToParent) sendDataToParent(next);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    propagate({ ...formData, [name]: value });
  };

  const handleToggle = (name, checked) => {
    propagate({ ...formData, [name]: checked });
  };

  if (readOnly) {
    return (
      <div className="row">
        <div className="col-lg-6 mb-2"><strong>Vendor Name:</strong> {formData.VendorName || '-'}</div>
        <div className="col-lg-6 mb-2"><strong>Email:</strong> {formData.Email || '-'}</div>
        <div className="col-lg-6 mb-2"><strong>Contact Number:</strong> {formData.ContactNumber || '-'}</div>
        <div className="col-lg-6 mb-2"><strong>Website:</strong> {formData.Website || '-'}</div>
        <div className="col-lg-12 mb-2"><strong>Address:</strong> {formData.Address || '-'}</div>
        <VendorLogoField formData={formData} readOnly />
        <div className="col-lg-6 mb-2"><strong>Rating:</strong> {formData.Rating ?? '-'}</div>
        <div className="col-lg-6 mb-2"><strong>Review Count:</strong> {formData.ReviewCount ?? 0}</div>
        <div className="col-lg-6 mb-2"><strong>Active:</strong> {formData.IsActive ? 'Yes' : 'No'}</div>
        {formData.CreatedDate ? (
          <div className="col-lg-6 mb-2"><strong>Created:</strong> {formData.CreatedDate}</div>
        ) : null}
        {formData.ModifiedDate ? (
          <div className="col-lg-6 mb-2"><strong>Modified:</strong> {formData.ModifiedDate}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-lg-6">
        <div className="mb-3">
          <label className="form-label">
            Vendor Name<span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="VendorName"
            value={formData.VendorName || ''}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="col-lg-6">
        <div className="mb-3">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            name="Email"
            value={formData.Email || ''}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="col-lg-6">
        <div className="mb-3">
          <label className="form-label">Contact Number</label>
          <input
            type="text"
            className="form-control"
            name="ContactNumber"
            value={formData.ContactNumber || ''}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="col-lg-6">
        <div className="mb-3">
          <label className="form-label">Website</label>
          <input
            type="text"
            className="form-control"
            name="Website"
            placeholder="https://example.com"
            value={formData.Website || ''}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="col-lg-12">
        <div className="mb-3">
          <label className="form-label">Address</label>
          <textarea
            className="form-control"
            name="Address"
            rows={2}
            value={formData.Address || ''}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="col-lg-12">
        <VendorLogoField formData={formData} sendDataToParent={sendDataToParent} />
      </div>
      <div className="col-lg-6">
        <div className="mb-3">
          <label className="form-label">Rating (0-5)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="5"
            className="form-control"
            name="Rating"
            value={formData.Rating ?? ''}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="col-lg-6">
        <div className="mb-3">
          <label className="form-label">Review Count</label>
          <input
            type="number"
            min="0"
            className="form-control"
            name="ReviewCount"
            value={formData.ReviewCount ?? 0}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="col-lg-6">
        <BooleanToggle
          id={`IsActive-${formData.VendorId || 'new'}`}
          label="Active"
          checked={formData.IsActive}
          onChange={(v) => handleToggle('IsActive', v)}
        />
      </div>
    </div>
  );
}

export default VendorDetailsFields;
